import React, { useEffect, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import PropTypes from 'prop-types';
import { makeStyles, Grid, Drawer } from '@material-ui/core';
import { Notifications, FiberManualRecord } from '@material-ui/icons';
import { orange } from '@material-ui/core/colors';
import { usePageVisibility } from 'react-page-visibility';
import CustomButtonIcon from '../Common/ButtonIcon';
import KillshotNotificationsDrawer from './Drawer';
import store from '../../redux/store';
import useIsTabActive from '../../hooks/useIsTabActive';
import { userQueryGetNotifications } from '../../hooks/eventNotifications';
import { getUserId, getLogUserOrg } from '../../helpers/user';

const useStyles = makeStyles({
  newNotificationFadeOut: {
    color: orange[500],
    marginTop: '-10px',
    opacity: 0,
    transition: 'opacity 1s',
  },
  newNotificationFadeIn: {
    color: orange[500],
    marginTop: '-10px',
    opacity: 1,
    transition: 'opacity 1s',
  },
  drawerPaper: {
    top: '70px',
    width: '250px',
    height: '-webkit-fill-available',
    overflowX: 'hidden',
  },
});

const KillshotNotifications = props => {
  const {
    earlierData,
    newData,
    notificationOpen,
    setEarlierData,
    setNewData,
    setNotificationOpen,
    setLastJsonMessageParent,
    setPopupOpen,
  } = props;
  const { user } = store.getState();
  const classes = useStyles();
  const [hasUnreadNotification, setHasUnreadNotification] = useState(false);
  const { WS_ENDPOINT } = process.env;
  const isTabActive = useIsTabActive();
  const isTabVisible = usePageVisibility();
  const { dataNotification, isFetchingNotification, dataUpdatedAt, refetch } = userQueryGetNotifications();
  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    WS_ENDPOINT,
    {
      share: true,
      shouldReconnect: () => true,
    },
    isTabVisible && isTabActive
  );

  const mapData = dataToMap => {
    const userOrganizations = user?.organizations || [];

    return dataToMap
      .filter(item => userOrganizations.some(org => org.id === item.org_id))
      .map(item => {
        const matchingOrg = userOrganizations.find(org => org.id === item.org_id);
        const orgName = matchingOrg ? matchingOrg.name : '';

        return {
          eventId: item.event_id,
          notificationId: item.notification_id,
          eventName: item.event_name,
          eventDate: item.event_datetime,
          groupName: item.group_name,
          type: item.type,
          org: orgName,
          datetime: item.notification_date,
          isRead: Boolean(item.user_read),
        };
      });
  };

  useEffect(() => {
    // Check if tab is visible to validate if timestamp for latest fetch is lower than timestamp from latest notification received
    if (isTabVisible && isTabActive) {
      const latestNotification = localStorage.getItem('latest_notification');
      if (latestNotification && dataUpdatedAt && dataUpdatedAt < latestNotification) {
        refetch();
      }
    }
  }, [isTabVisible, isTabActive]);

  useEffect(() => {
    if (lastJsonMessage) {
      setPopupOpen(true);
      // Set timestamp for latest notification received
      localStorage.setItem('latest_notification', Date.now());
      refetch();
      setLastJsonMessageParent(lastJsonMessage);
    }
  }, [lastJsonMessage]);
  useEffect(() => {
    if (dataNotification && dataNotification.response) {
      setNewData(mapData(dataNotification?.response.new));
      setEarlierData(mapData(dataNotification?.response.earlier));
    }
  }, [dataNotification]);

  useEffect(() => {
    if (readyState === ReadyState.OPEN && isTabVisible) {
      sendJsonMessage({
        action: 'subscribe',
        user_id: getUserId(),
        org_id: getLogUserOrg(),
      });
    }
  }, [readyState]);
  useEffect(() => {
    const flag = newData.some(item => !item.isRead) || earlierData.some(item => !item.isRead);
    setHasUnreadNotification(flag);
  }, [newData, earlierData]);

  return (
    <>
      <Grid container direction="row">
        <Grid item>
          <CustomButtonIcon
            tooltip
            tooltipTitle="Click to see new inventory"
            size="large"
            onClick={() => {
              setNotificationOpen(true);
            }}
            width="40px"
            height="40px"
          >
            <Notifications style={{ fontSize: '25px' }} />
          </CustomButtonIcon>
        </Grid>
        <Grid item>
          <FiberManualRecord
            className={hasUnreadNotification ? classes.newNotificationFadeIn : classes.newNotificationFadeOut}
            style={{ fontSize: '16px', marginLeft: '-18px' }}
          />
        </Grid>
        <Drawer
          classes={{
            paper: classes.drawerPaper,
          }}
          anchor="right"
          open={notificationOpen}
          onClose={() => setNotificationOpen(false)}
          variant="persistent"
        >
          {notificationOpen && (
            <KillshotNotificationsDrawer
              earlierData={earlierData}
              newData={newData}
              isFetching={isFetchingNotification}
              setEarlierData={setEarlierData}
              setNewData={setNewData}
              setNotificationOpen={setNotificationOpen}
            />
          )}
        </Drawer>
      </Grid>
    </>
  );
};

KillshotNotifications.propTypes = {
  earlierData: PropTypes.array.isRequired,
  newData: PropTypes.array.isRequired,
  notificationOpen: PropTypes.bool.isRequired,
  setEarlierData: PropTypes.func.isRequired,
  setNewData: PropTypes.func.isRequired,
  setNotificationOpen: PropTypes.func.isRequired,
  setLastJsonMessageParent: PropTypes.func.isRequired,
  setPopupOpen: PropTypes.func.isRequired,
};

export default KillshotNotifications;
